import { useState, useEffect, FormEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Anchor, CheckCircle, ArrowRight, Loader2, Play, X } from "lucide-react";
import Footer from "@/components/Footer";
import { usePageSEO } from "@/hooks/use-page-seo";
import { saveCreatorApplication, n8nSendCreatorText } from "@/lib/firebase";
import logoWhite from "@/assets/logo-white.svg";

const NAVY = "#0B1622";

const SUNSET = "/creators/hero.jpg";
// Public Creator Crew signups are closed once the comp pool is full.
// Flip to false to reopen the join form.
const CAPACITY_REACHED = true;

// 2025 recap video (Cloudflare Stream — same asset as the links page).
const RECAP_VIDEO_SRC =
  "https://customer-izud1idylkfqkoa2.cloudflarestream.com/58bd12339bafa1040d9b189e120f6f57/iframe?autoplay=true&poster=https%3A%2F%2Fcustomer-izud1idylkfqkoa2.cloudflarestream.com%2F58bd12339bafa1040d9b189e120f6f57%2Fthumbnails%2Fthumbnail.jpg";

const steps = [
  {
    n: "01",
    title: "Join the crew",
    body: "Drop your info below — it takes a minute. No follower minimum, we don't care how big you are.",
  },
  {
    n: "02",
    title: "Repost + add to story",
    body: "Repost our pinned post for 1 free ticket. Add it to your story for 1 more. Do both and that's 2 free tickets.",
  },
  {
    n: "03",
    title: "Go for the grand prize",
    body: "Throw on a captain's hat, snap a pic, and tag @yachtrockdetroit — you're entered to win 6 VIP passes + a private tiki-boat ride.",
  },
];

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
};

const Star = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2l2.9 6.2 6.8.8-5 4.6 1.3 6.7L12 17.8 5.9 20.3 7.2 13.6l-5-4.6 6.8-.8z" />
  </svg>
);

const marqueeItems = [
  "Free Two-Day Pass",
  "25+ Live Acts",
  "Captain Hats Encouraged",
  "Floating DJ Deck",
  "Sunset on the Water",
  "No Followers Needed",
];

const Creators = () => {
  usePageSEO({
    title: "Join the Yacht Rock Detroit Creator Crew",
    description:
      "Join the YRD Creator Crew and your weekend's on us. Post a captain-hat pic, bring a +1, and get a shot at 6 VIP passes and a private tiki-boat ride. July 17–18, 2026 at The Roostertail.",
    canonical: "https://yachtrockdetroit.com/creators",
    ogImage: "https://yachtrockdetroit.com/creators/poster.jpg",
  });

  const [status, setStatus] = useState<"idle" | "saving" | "done" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [videoOpen, setVideoOpen] = useState(false);

  // Close the recap lightbox on Escape.
  useEffect(() => {
    if (!videoOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setVideoOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [videoOpen]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    const fd = new FormData(form);
    const firstName = String(fd.get("firstName") || "").trim();
    const lastName = String(fd.get("lastName") || "").trim();
    const email = String(fd.get("email") || "").trim();
    const phone = String(fd.get("phone") || "").trim();
    const instagram = String(fd.get("instagram") || "").trim().replace(/^@/, "");
    const dob = String(fd.get("dob") || "").trim();
    setStatus("saving");
    setErrorMsg("");
    try {
      await saveCreatorApplication({ firstName, lastName, email, phone, instagram, dob });
      // Fire the SlickText "thanks for joining" text — don't block the UI on it.
      n8nSendCreatorText({ phone, firstName, instagram }).catch((e) => console.error("creator text webhook failed", e));
      // Meta Pixel Lead event (existing pixel 1617013252635226) so ads can optimize for signups.
      if (typeof window !== "undefined" && (window as any).fbq) {
        (window as any).fbq("track", "Lead", { content_name: "creator-apply" });
      }
      setStatus("done");
      setTimeout(() => {
        document.getElementById("apply")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
    } catch (err) {
      console.error("creator application failed", err);
      setStatus("error");
      setErrorMsg("Something went wrong — please try again.");
    }
  };

  const inputClass =
    "w-full min-w-0 text-left bg-white/[0.04] border border-white/15 rounded-xl px-4 py-3.5 text-yacht-cream placeholder:text-white/30 text-[15px] outline-none transition focus:border-yacht-seaglass focus:ring-2 focus:ring-yacht-seaglass/25";
  const labelClass = "block text-[11px] font-bold uppercase tracking-[0.12em] text-white/55 mb-2";

  return (
    <div className="min-h-screen text-yacht-cream" style={{ backgroundColor: NAVY }}>
      {/* HERO — focused landing: just a logo, no nav, so they fill the form. */}
      <section className="relative flex flex-col md:flex-row md:items-start overflow-hidden md:min-h-screen">
        {/* Brand logo only — no nav bar. */}
        <a href="/" className="absolute top-5 left-1/2 -translate-x-1/2 z-20">
          <img src={logoWhite} alt="Yacht Rock Detroit" className="h-14 md:h-16 w-auto" />
        </a>
        {/* Desktop only: the crew image. */}
        <motion.div
          className="absolute inset-0 hidden md:block bg-cover bg-right"
          style={{ backgroundImage: `url(${SUNSET})` }}
          initial={{ scale: 1.15 }}
          animate={{ scale: 1.04 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        />
        {/* Desktop: left-to-dark gradient so the headline pops over the blank left side. */}
        <div
          className="absolute inset-0 hidden md:block"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,22,34,.94) 0%, rgba(11,22,34,.78) 38%, rgba(11,22,34,.30) 70%, rgba(11,22,34,.20) 100%)",
          }}
        />
        {/* Mobile: subtle glow behind the headline for depth. */}
        <div
          className="absolute inset-x-0 top-0 h-[70%] md:hidden pointer-events-none"
          style={{
            background:
              "radial-gradient(120% 80% at 50% 8%, rgba(56,159,186,.20), transparent 60%), radial-gradient(120% 60% at 50% 60%, rgba(168,85,247,.14), transparent 60%)",
          }}
        />

        <div className="relative z-10 w-full max-w-3xl mx-auto md:mx-0 px-6 md:pl-16 lg:pl-24 text-center md:text-left pt-28 md:pt-32 pb-12 md:pb-20">
          <div className="flex items-center justify-center md:justify-start gap-3 mb-7 md:mb-8 flex-wrap animate-fade-up">
            <span className="inline-flex items-center gap-1.5 bg-yacht-seaglass text-yacht-charcoal font-['Anton'] uppercase tracking-wide text-sm px-4 py-2 rounded-full border-2 border-white/85 -rotate-2">
              <Anchor className="w-4 h-4" /> {CAPACITY_REACHED ? "Crew Full" : "Join the Crew"}
            </span>
            <span className="inline-flex items-center bg-yacht-yellow text-yacht-charcoal font-['Anton'] uppercase tracking-wide text-sm px-4 py-2 rounded-full border-2 border-white/85 rotate-2">
              July 17–18<span className="hidden sm:inline"> · Detroit River</span>
            </span>
          </div>
          <h1 className="font-['Anton'] uppercase leading-[0.92] text-[clamp(2.75rem,12vw,4.6rem)] animate-fade-up">
            Your weekend<br />
            on the river,<br />
            <span className="text-yacht-yellow">on us.</span>
          </h1>
          <p className="mt-6 md:mt-7 mx-auto md:mx-0 max-w-xl text-lg md:text-lg text-yacht-cream/85 leading-relaxed animate-fade-up">
            {CAPACITY_REACHED ? (
              <>The Creator Crew is <span className="text-yacht-yellow">full</span> — thank you to everyone who jumped aboard. We're still sending passes to our top creators who keep reposting and tagging <span className="text-yacht-cream">@yachtrockdetroit.</span></>
            ) : (
              <>Repost our post for a free ticket, add it to your story for a second — that's 2 free tickets, no follower count required. Post a captain-hat pic and you're in to win <span className="text-yacht-cream">6 VIP passes and a private tiki-boat ride.</span></>
            )}
          </p>
          <div className="mt-8 md:mt-10 animate-fade-up flex flex-col sm:flex-row items-center md:items-start gap-4 sm:gap-6">
            <a
              href="#apply"
              className="inline-flex items-center gap-2 bg-yacht-yellow text-yacht-charcoal font-['Anton'] uppercase tracking-wide text-lg md:text-base px-9 py-4 rounded-full shadow-[0_8px_0_#b5830f] hover:-translate-y-0.5 hover:shadow-[0_11px_0_#b5830f] active:translate-y-0 active:shadow-[0_4px_0_#b5830f] transition-all"
            >
              {CAPACITY_REACHED ? "Still Want In?" : "Join the Crew"} <ArrowRight className="w-5 h-5" />
            </a>
            <button
              type="button"
              onClick={() => setVideoOpen(true)}
              className="group inline-flex items-center gap-2.5 text-yacht-cream/85 hover:text-yacht-cream transition-colors"
            >
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-white/10 border border-white/25 group-hover:bg-white/20 transition">
                <Play className="w-4 h-4 fill-current" />
              </span>
              <span className="text-sm font-semibold underline-offset-4 group-hover:underline">Learn about the event</span>
            </button>
          </div>
        </div>

        {/* Mobile only: crew photo at the bottom in a wide 21:9 frame. */}
        <div className="md:hidden relative w-full aspect-[21/9] mt-2">
          <div
            className="absolute inset-0 bg-cover"
            style={{ backgroundImage: `url(${SUNSET})`, backgroundPosition: "center 28%" }}
          />
          {/* Top fade so the photo blends out of the navy headline area. */}
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to bottom, #0B1622 0%, rgba(11,22,34,0) 32%, rgba(11,22,34,0) 100%)" }}
          />
        </div>
      </section>

      {/* MARQUEE */}
      <div className="relative overflow-hidden border-y border-white/10 bg-yacht-blue/[0.08] py-3.5">
        <motion.div
          className="flex whitespace-nowrap"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
        >
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center shrink-0">
              {marqueeItems.map((item, i) => (
                <span key={`${copy}-${i}`} className="flex items-center">
                  <span className={`font-['Anton'] uppercase tracking-wide text-sm ${i % 2 === 0 ? "text-yacht-yellow" : "text-yacht-cream/70"}`}>
                    {item}
                  </span>
                  <Star className="w-3.5 h-3.5 mx-5 text-yacht-seaglass shrink-0" />
                </span>
              ))}
            </div>
          ))}
        </motion.div>
      </div>

      {/* THE DEAL */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fade} className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-yacht-seaglass text-xs font-bold tracking-[0.22em] uppercase mb-5">The Deal</div>
            <h2 className="font-['Anton'] uppercase leading-[1] text-[clamp(1.9rem,4.4vw,3.2rem)]">
              Dead simple. <span className="text-yacht-yellow">Three steps.</span>
            </h2>
            <p className="mt-6 text-lg text-yacht-cream/75 leading-relaxed">
              We're packing the Detroit River with people who love this music — July 17–18, 2026. Help us bring the
              energy and the weekend's on us.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Step 1 — Repost */}
            <motion.div {...fade} className="relative rounded-2xl p-8 bg-white/[0.03] border border-white/10">
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] mb-3 text-yacht-seaglass">Step 1 · Repost</div>
              <div className="font-['Anton'] text-5xl leading-none">1 Ticket</div>
              <div className="text-yacht-yellow text-[13px] font-semibold mt-1 mb-4">Full two-day pass</div>
              <p className="text-yacht-cream/70 text-[15px] leading-relaxed">
                Repost our pinned post to your feed. That's 1 free two-day ticket — no follower minimum, no catch.
              </p>
            </motion.div>

            {/* Step 2 — Add to story */}
            <motion.div
              {...fade}
              transition={{ ...fade.transition, delay: 0.08 }}
              className="relative rounded-2xl p-8 bg-yacht-yellow/[0.06] border border-yacht-yellow/60"
            >
              <span className="absolute -top-3 right-5 bg-yacht-yellow text-yacht-charcoal text-[10px] font-['Anton'] uppercase tracking-wide px-3 py-1 rounded-full">
                2 total
              </span>
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] mb-3 text-yacht-yellow">Step 2 · Add to your story</div>
              <div className="font-['Anton'] text-5xl leading-none">2 Tickets</div>
              <div className="text-yacht-yellow text-[13px] font-semibold mt-1 mb-4">Do both — that's 2 free passes</div>
              <p className="text-yacht-cream/70 text-[15px] leading-relaxed">
                Share that same post to your story too. That's your second free ticket — <span className="text-yacht-cream">2 in total.</span>
              </p>
            </motion.div>

            {/* Step 3 — Captain hat → grand prize */}
            <motion.div {...fade} transition={{ ...fade.transition, delay: 0.16 }} className="relative rounded-2xl p-8 bg-white/[0.03] border border-white/10">
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] mb-3 text-yacht-seaglass">Bonus · Captain-hat pic</div>
              <div className="font-['Anton'] text-5xl leading-none">6 VIP</div>
              <div className="text-yacht-yellow text-[13px] font-semibold mt-1 mb-4">+ a private tiki-boat ride</div>
              <p className="text-yacht-cream/70 text-[15px] leading-relaxed">
                Post a pic in a captain's hat and tag <b className="text-yacht-cream">@yachtrockdetroit</b> — you're entered to win the grand prize.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* GRAND PRIZE */}
      <section className="relative overflow-hidden border-y border-white/[0.06] py-20 md:py-24 px-6">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 90% at 50% 0%, rgba(247,180,28,.12), transparent 55%), radial-gradient(120% 90% at 50% 100%, rgba(56,159,186,.12), transparent 55%)",
          }}
        />
        <motion.div {...fade} className="relative z-10 max-w-2xl mx-auto text-center">
          <div className="text-yacht-yellow text-xs font-bold tracking-[0.22em] uppercase mb-5">The Grand Prize</div>
          <h2 className="font-['Anton'] uppercase leading-[1] text-[clamp(2rem,5vw,3.4rem)]">
            6 VIP passes <span className="text-yacht-yellow">+ a private tiki-boat ride.</span>
          </h2>
          <p className="mt-6 text-lg text-yacht-cream/75 leading-relaxed">
            One crew wins it all — six VIP passes and a private Aloha Tiki Tours boat on the Detroit River, July 18.
            Every captain-hat pic that tags us is an entry. Post yours.
          </p>
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fade} className="text-center mb-16">
            <div className="text-yacht-seaglass text-xs font-bold tracking-[0.22em] uppercase mb-4">How it works</div>
            <h2 className="font-['Anton'] uppercase text-[clamp(1.8rem,4vw,3rem)]">
              Three steps to the <span className="text-yacht-yellow">deck.</span>
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-12 md:gap-10">
            {steps.map((s, i) => (
              <motion.div key={s.n} {...fade} transition={{ ...fade.transition, delay: i * 0.08 }} className="text-center md:text-left">
                <div className="font-['Anton'] text-5xl text-yacht-blue/90 mb-4">{s.n}</div>
                <h3 className="font-['Anton'] uppercase tracking-wide text-xl mb-3">{s.title}</h3>
                <p className="text-yacht-cream/70 leading-relaxed text-[15px]">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLY / JOIN */}
      <section id="apply" className="py-24 md:py-32 px-6 scroll-mt-24 border-t border-white/[0.06]">
        <div className="max-w-2xl mx-auto">
          {CAPACITY_REACHED ? (
            <motion.div {...fade} className="text-center py-6">
              <div className="text-yacht-yellow text-xs font-bold tracking-[0.22em] uppercase mb-4">Crew Full</div>
              <h2 className="font-['Anton'] uppercase text-[clamp(1.9rem,4.4vw,3.2rem)] leading-[1]">
                We've reached <span className="text-yacht-yellow">capacity</span> ⚓
              </h2>
              <p className="mt-5 text-yacht-cream/75 leading-relaxed max-w-md mx-auto">
                The Creator Crew is full — thank you to everyone who jumped aboard! 🛥️
              </p>
              <div className="mt-8 text-left max-w-md mx-auto rounded-2xl border border-yacht-yellow/40 bg-yacht-yellow/[0.06] p-6">
                <p className="text-yacht-cream/85 leading-relaxed">
                  Still want in? We're <b className="text-yacht-yellow">still sending passes to our top creators</b> — the ones who consistently repost and share our posts. Keep reposting and tagging <span className="text-yacht-seaglass">@yachtrockdetroit</span> and you could still end up at the festival.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                className="group mt-7 inline-flex items-center gap-2.5 text-yacht-seaglass hover:text-yacht-cream transition-colors"
              >
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 border border-white/25 group-hover:bg-white/20 transition">
                  <Play className="w-3.5 h-3.5 fill-current" />
                </span>
                <span className="text-sm font-semibold underline-offset-4 group-hover:underline">Learn about the event</span>
              </button>
            </motion.div>
          ) : status !== "done" ? (
            <motion.div {...fade}>
              <div className="text-center mb-12">
                <div className="text-yacht-seaglass text-xs font-bold tracking-[0.22em] uppercase mb-4">Join the Crew</div>
                <h2 className="font-['Anton'] uppercase text-[clamp(1.9rem,4.4vw,3.2rem)]">
                  Claim your <span className="text-yacht-yellow">free pass</span>
                </h2>
                <p className="mt-5 text-yacht-cream/70 leading-relaxed max-w-md mx-auto">
                  Fill this out and we'll text you how to claim your free tickets — repost our post + add it to your story.
                </p>
                <button
                  type="button"
                  onClick={() => setVideoOpen(true)}
                  className="group mt-5 inline-flex items-center gap-2.5 text-yacht-seaglass hover:text-yacht-cream transition-colors"
                >
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 border border-white/25 group-hover:bg-white/20 transition">
                    <Play className="w-3.5 h-3.5 fill-current" />
                  </span>
                  <span className="text-sm font-semibold underline-offset-4 group-hover:underline">Learn about the event</span>
                </button>
              </div>

              <form onSubmit={handleSubmit} noValidate className="space-y-5 text-left">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>First name *</label>
                    <input name="firstName" autoComplete="given-name" required placeholder="First" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Last name *</label>
                    <input name="lastName" autoComplete="family-name" required placeholder="Last" className={inputClass} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>Email *</label>
                    <input name="email" type="email" inputMode="email" autoComplete="email" required placeholder="you@email.com" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Phone *</label>
                    <input name="phone" type="tel" inputMode="tel" autoComplete="tel" required placeholder="(313) 555-0123" className={inputClass} />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Instagram handle *</label>
                  <div className="flex items-center bg-white/[0.04] border border-white/15 rounded-xl overflow-hidden focus-within:border-yacht-seaglass focus-within:ring-2 focus-within:ring-yacht-seaglass/25 transition">
                    <span className="px-3.5 text-white/45 border-r border-white/12">@</span>
                    <input name="instagram" autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false} required placeholder="yourhandle" className="flex-1 min-w-0 bg-transparent px-3.5 py-3.5 text-yacht-cream placeholder:text-white/30 text-[15px] outline-none" />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Date of birth *</label>
                  <input name="dob" type="date" autoComplete="bday" required className={`${inputClass} [color-scheme:dark] appearance-none`} />
                </div>

                {status === "error" && <p className="text-red-400 text-sm text-center">{errorMsg}</p>}

                <button
                  type="submit"
                  disabled={status === "saving"}
                  className="w-full bg-yacht-yellow text-yacht-charcoal font-['Anton'] uppercase tracking-wide text-lg py-4 rounded-xl shadow-[0_8px_0_#b5830f] hover:-translate-y-0.5 hover:shadow-[0_11px_0_#b5830f] active:translate-y-0 active:shadow-[0_4px_0_#b5830f] transition-all mt-2 disabled:opacity-70 disabled:translate-y-0 flex items-center justify-center gap-2"
                >
                  {status === "saving" ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Joining…
                    </>
                  ) : (
                    "Join the Crew"
                  )}
                </button>
                <p className="text-center text-[12.5px] text-white/45 leading-relaxed">
                  Must be 18+. By joining you agree to receive texts about your tickets. Repost our post = 1 free ticket,
                  add it to your story = 1 more. Posting a captain-hat pic and tagging
                  <span className="text-yacht-seaglass"> @yachtrockdetroit</span> enters you to win 6 VIP + the tiki boat — no purchase necessary.
                </p>
              </form>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-6">
              <CheckCircle className="w-14 h-14 text-yacht-seaglass mx-auto mb-5" />
              <h2 className="font-['Anton'] uppercase text-3xl md:text-4xl text-yacht-seaglass leading-[1]">
                You're on the crew! ⚓
              </h2>
              <p className="mt-4 text-yacht-cream/80 leading-relaxed max-w-md mx-auto">
                Welcome aboard! To grab your free tickets: <b>repost our pinned post</b> (1 ticket) and <b>add it to your story</b> (1 more) — that's 2 free tickets. We'll text you the details shortly.
              </p>

              <div className="mt-8 text-left max-w-sm mx-auto rounded-2xl border border-yacht-yellow/40 bg-yacht-yellow/[0.06] p-6">
                <div className="font-['Anton'] uppercase tracking-wide text-yacht-yellow text-sm mb-3">
                  Want a shot at the grand prize?
                </div>
                <p className="text-yacht-cream/85 text-[15px] leading-relaxed">
                  Post a pic in a captain's hat and tag <b>@yachtrockdetroit</b> — you're entered to win <b>6 VIP + a private tiki-boat ride.</b>
                </p>
              </div>

              <a
                href="/"
                className="inline-flex items-center gap-2 mt-8 bg-yacht-yellow text-yacht-charcoal font-['Anton'] uppercase tracking-wide px-8 py-3.5 rounded-full shadow-[0_8px_0_#b5830f] hover:-translate-y-0.5 hover:shadow-[0_11px_0_#b5830f] active:translate-y-0 active:shadow-[0_4px_0_#b5830f] transition-all"
              >
                Explore the event <ArrowRight className="w-5 h-5" />
              </a>
              <p className="mt-6 text-yacht-cream/70">See you on the river ⚓</p>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />

      {/* RECAP VIDEO LIGHTBOX */}
      <AnimatePresence>
        {videoOpen && (
          <motion.div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setVideoOpen(false)}
          >
            <motion.div
              className="relative w-full max-w-4xl aspect-video"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setVideoOpen(false)}
                aria-label="Close video"
                className="absolute -top-11 right-0 inline-flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-semibold"
              >
                Close <X className="w-5 h-5" />
              </button>
              <iframe
                src={RECAP_VIDEO_SRC}
                title="Yacht Rock Detroit 2025 recap"
                className="w-full h-full rounded-xl border border-white/10 bg-black"
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Creators;
